/**
 * Cost Per Carat Analysis Section
 * Extracted from FinancePage.jsx
 */

import React from 'react';
import { Gem, Activity, Target, ArrowLeft, Info } from 'lucide-react';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const CostPerCaratDetail = ({ year, language, setActiveSection, flowMap }) => {
  const cpcData = mockData.costPerCarat?.[year] || mockData.costPerCarat[2025];
  const prevCpcData = mockData.costPerCarat?.[year - 1] || mockData.costPerCarat[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Budget estimates for cost per carat (realistic estimation)
  // Actual cost is around 158 USD/ct, budget target is typically 150-165 USD/ct
  const budgetCostPerCarat = 152; // Target budget cost per carat
  const costPerCaratVsBudget = ((cpcData.kpis.costPerCarat.value - budgetCostPerCarat) / budgetCostPerCarat * 100).toFixed(1);
  
  // Budget values for operational metrics
  const budgetWasteMoved = 16.1; // M m³
  const budgetOreMoved = 18.4; // M m³
  const budgetOreProcessed = 2.4; // Mt
  const budgetTotalOpCost = 125; // M USD
  
  // Budget values for drivers
  const budgetGrade = 34.0; // ct/100t
  const budgetRecoveryRate = 92.0; // %
  const budgetStripRatio = 7.0; // x
  const budgetPlantAvailability = 90.0; // %
  
  // Calculate budget variances
  const wasteMovedVsBudget = ((cpcData.drivers.wasteMoved.value - budgetWasteMoved) / budgetWasteMoved * 100).toFixed(1);
  const oreMovedVsBudget = ((cpcData.kpis.materialMoved.value - budgetOreMoved) / budgetOreMoved * 100).toFixed(1);
  const oreProcessedVsBudget = ((cpcData.drivers.oreProcessed.value - budgetOreProcessed) / budgetOreProcessed * 100).toFixed(1);
  const opCostVsBudget = ((cpcData.kpis.totalOpCost.value - budgetTotalOpCost) / budgetTotalOpCost * 100).toFixed(1);
  
  // Calculate driver budget variances
  const gradeVsBudget = ((cpcData.drivers.grade.value - budgetGrade) / budgetGrade * 100).toFixed(1);
  const recoveryVsBudget = ((cpcData.drivers.recoveryRate.value - budgetRecoveryRate) / budgetRecoveryRate * 100).toFixed(1);
  const stripRatioVsBudget = (cpcData.drivers.stripRatio.value - budgetStripRatio).toFixed(1);
  const plantAvailVsBudget = ((cpcData.drivers.plantAvailability.value - budgetPlantAvailability) / budgetPlantAvailability * 100).toFixed(1);

  const costPerCaratInsights = {
    pt: [
      { title: 'DENOMINADOR', description: 'Custo sobe se grade ou recuperação caírem, mesmo sem aumento de custos.', color: 'text-emerald-400' },
      { title: 'VARIABILIDADE', description: 'Diluição, strip ratio e qualidade do stockpile impactam custo unitário.', color: 'text-white' },
      { title: 'KPI FINAL', description: 'Deve ser lido com drivers operacionais e geológicos em conjunto.', color: 'text-amber-400' }
    ],
    en: [
      { title: 'DENOMINATOR', description: 'Cost rises if grade or recovery drop, even without cost increases.', color: 'text-emerald-400' },
      { title: 'VARIABILITY', description: 'Dilution, strip ratio and stockpile quality impact unit cost.', color: 'text-white' },
      { title: 'FINAL KPI', description: 'Must be read with operational and geological drivers together.', color: 'text-amber-400' }
    ]
  };

  return (
    <div className="space-y-6" data-testid="cost-per-carat-section">
      {/* Page Title */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Custo Unitário por Quilate' : 'Unit Cost per Carat'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {costPerCaratInsights[language].map((insight, idx) => (
            <div 
              key={idx} 
              className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  idx === 0 ? 'bg-emerald-500/30' : idx === 1 ? 'bg-slate-500/30' : 'bg-amber-500/30'
                }`}>
                  {idx === 0 ? (
                    <Gem className="w-5 h-5 text-emerald-400" />
                  ) : idx === 1 ? (
                    <Activity className="w-5 h-5 text-slate-300" />
                  ) : (
                    <Target className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5 KPIs */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-amber-200 bg-amber-50/30 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Custo / Quilate' : 'Cost / Carat'}</p>
          <p className="font-serif text-3xl font-semibold text-amber-600">{cpcData.kpis.costPerCarat.value}<span className="text-base text-amber-400 ml-1">USD/ct</span></p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${parseFloat(costPerCaratVsBudget) > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {costPerCaratVsBudget > 0 ? '+' : ''}{costPerCaratVsBudget}% Orç.
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cpcData.kpis.costPerCarat.value > prevCpcData.kpis.costPerCarat.value ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {cpcData.kpis.costPerCarat.value > prevCpcData.kpis.costPerCarat.value ? '+' : ''}{Math.round((cpcData.kpis.costPerCarat.value / prevCpcData.kpis.costPerCarat.value - 1) * 100)}% YoY
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Estéril Movido' : 'Waste Moved'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{cpcData.drivers.wasteMoved.value}<span className="text-base text-slate-400 ml-1">{cpcData.drivers.wasteMoved.unit}</span></p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(wasteMovedVsBudget) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {wasteMovedVsBudget > 0 ? '+' : ''}{wasteMovedVsBudget}% Orç.
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${cpcData.drivers.wasteMoved.vsLY >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {cpcData.drivers.wasteMoved.vsLY > 0 ? '+' : ''}{cpcData.drivers.wasteMoved.vsLY}% YoY
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Minério Movido' : 'Ore Moved'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{cpcData.kpis.materialMoved.value}<span className="text-base text-slate-400 ml-1">{cpcData.kpis.materialMoved.unit}</span></p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(oreMovedVsBudget) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {oreMovedVsBudget > 0 ? '+' : ''}{oreMovedVsBudget}% Orç.
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${(cpcData.kpis.materialMoved.value / prevCpcData.kpis.materialMoved.value - 1) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {Math.round((cpcData.kpis.materialMoved.value / prevCpcData.kpis.materialMoved.value - 1) * 100) > 0 ? '+' : ''}{Math.round((cpcData.kpis.materialMoved.value / prevCpcData.kpis.materialMoved.value - 1) * 100)}% YoY
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Minério Tratado' : 'Ore Processed'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{cpcData.drivers.oreProcessed.value}<span className="text-base text-slate-400 ml-1">{cpcData.drivers.oreProcessed.unit}</span></p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(oreProcessedVsBudget) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {oreProcessedVsBudget > 0 ? '+' : ''}{oreProcessedVsBudget}% Orç.
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${cpcData.drivers.oreProcessed.vsLY >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {cpcData.drivers.oreProcessed.vsLY > 0 ? '+' : ''}{cpcData.drivers.oreProcessed.vsLY}% YoY
            </span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Custo Operacional' : 'Operational Cost'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{cpcData.kpis.totalOpCost.value}<span className="text-base text-slate-400 ml-1">{cpcData.kpis.totalOpCost.unit}</span></p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(opCostVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {opCostVsBudget > 0 ? '+' : ''}{opCostVsBudget}% Orç.
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${cpcData.kpis.totalOpCost.value <= prevCpcData.kpis.totalOpCost.value ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {cpcData.kpis.totalOpCost.value > prevCpcData.kpis.totalOpCost.value ? '+' : ''}{Math.round((cpcData.kpis.totalOpCost.value / prevCpcData.kpis.totalOpCost.value - 1) * 100)}% YoY
            </span>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custo por quilate e eficiência operacional' : 'Cost per carat and operational efficiency'}
        language={language}
        maxHeight="400px"
        icon={Info}
        dataTestId="cost-per-carat-executive-summary"
      >
        <div className="space-y-4">
          {[
            {
              pt: 'O custo por quilate é o principal indicador de eficiência operacional na mineração de diamantes, reflectindo a relação entre o custo total de extracção e o volume de quilates recuperados.',
              en: 'Cost per carat is the main operational efficiency indicator in diamond mining, reflecting the relationship between total extraction cost and recovered carat volume.'
            },
            {
              pt: 'Este indicador é sensível tanto a factores de custo (custos operacionais, materiais, mão-de-obra) como a factores geológicos (teor do minério, taxa de recuperação, strip ratio).',
              en: 'This indicator is sensitive to both cost factors (operating costs, materials, labor) and geological factors (ore grade, recovery rate, strip ratio).'
            },
            {
              pt: 'A variação do custo unitário deve ser analisada em conjunto com os drivers operacionais para distinguir entre ineficiências de custo e variações naturais de produtividade do jazigo.',
              en: 'Unit cost variation should be analyzed together with operational drivers to distinguish between cost inefficiencies and natural deposit productivity variations.'
            },
          ].map((item, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center mt-0.5">
                <span className="text-white text-xs font-bold">{idx + 1}</span>
              </div>
              <p className="text-slate-700 leading-relaxed text-sm">{item[language]}</p>
            </div>
          ))}
        </div>
      </ExecutiveSummarySection>

      {/* Two Analysis Sections - Cost Analysis and Operational Analysis */}
      <div className="grid grid-cols-2 gap-4">
        {/* Cost Analysis Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-serif text-base font-bold text-primary mb-1">
            {language === 'pt' ? 'Análise de Custo' : 'Cost Analysis'}
          </h3>
          <p className="text-slate-400 text-[10px] mb-4">
            {language === 'pt' ? '2025 vs Orçamento 2025 vs 2024' : 'Actual 2025 vs Budget 2025 vs Actual 2024'}
          </p>
          
          <div className="space-y-4">
            {/* Cost per Carat */}
            <div className="flex items-center gap-3">
              <div className="w-28 flex-shrink-0">
                <p className="text-xs font-medium text-primary">{language === 'pt' ? 'Custo/Quilate' : 'Cost/Carat'}</p>
                <p className="text-[10px] text-slate-400">USD/ct</p>
              </div>
              <div className="flex-1 flex gap-1 items-end h-16">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-primary mb-1">{cpcData.kpis.costPerCarat.value}</span>
                  <div className="w-full bg-gradient-to-t from-primary to-primary/80 rounded-t" style={{ height: `${(cpcData.kpis.costPerCarat.value / 160) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-gold-600 mb-1">{budgetCostPerCarat}</span>
                  <div className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t" style={{ height: `${(budgetCostPerCarat / 160) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] text-slate-400 mb-1">{prevCpcData.kpis.costPerCarat.value}</span>
                  <div className="w-full bg-gradient-to-t from-slate-300 to-slate-200 rounded-t" style={{ height: `${(prevCpcData.kpis.costPerCarat.value / 160) * 50}px` }}></div>
                </div>
              </div>
            </div>
            
            {/* Operational Cost */}
            <div className="flex items-center gap-3">
              <div className="w-28 flex-shrink-0">
                <p className="text-xs font-medium text-primary">{language === 'pt' ? 'Custo Operacional' : 'Operational Cost'}</p>
                <p className="text-[10px] text-slate-400">M USD</p>
              </div>
              <div className="flex-1 flex gap-1 items-end h-16">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-primary mb-1">{cpcData.kpis.totalOpCost.value}</span>
                  <div className="w-full bg-gradient-to-t from-primary to-primary/80 rounded-t" style={{ height: `${(cpcData.kpis.totalOpCost.value / 150) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-gold-600 mb-1">{budgetTotalOpCost}</span>
                  <div className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t" style={{ height: `${(budgetTotalOpCost / 150) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] text-slate-400 mb-1">{prevCpcData.kpis.totalOpCost.value}</span>
                  <div className="w-full bg-gradient-to-t from-slate-300 to-slate-200 rounded-t" style={{ height: `${(prevCpcData.kpis.totalOpCost.value / 150) * 50}px` }}></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-3 mt-4 pt-3 border-t border-slate-100 text-[9px]">
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-primary rounded"></div><span>2025</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-gold-500 rounded"></div><span>Orç. 2025</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-slate-300 rounded"></div><span>2024</span></div>
          </div>
        </div>

        {/* Operational Analysis Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-serif text-base font-bold text-primary mb-1">
            {language === 'pt' ? 'Análise Operacional' : 'Operational Analysis'}
          </h3>
          <p className="text-slate-400 text-[10px] mb-4">
            {language === 'pt' ? '2025 vs Orçamento 2025 vs 2024' : 'Actual 2025 vs Budget 2025 vs Actual 2024'}
          </p>
          
          <div className="space-y-4">
            {/* Carats Recovered */}
            <div className="flex items-center gap-3">
              <div className="w-28 flex-shrink-0">
                <p className="text-xs font-medium text-primary">{language === 'pt' ? 'Quilates Recup.' : 'Carats Recovered'}</p>
                <p className="text-[10px] text-slate-400">k ct</p>
              </div>
              <div className="flex-1 flex gap-1 items-end h-16">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-primary mb-1">920</span>
                  <div className="w-full bg-gradient-to-t from-primary to-primary/80 rounded-t" style={{ height: '50px' }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-gold-600 mb-1">880</span>
                  <div className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t" style={{ height: '48px' }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] text-slate-400 mb-1">820</span>
                  <div className="w-full bg-gradient-to-t from-slate-300 to-slate-200 rounded-t" style={{ height: '45px' }}></div>
                </div>
              </div>
            </div>
            
            {/* Ore Processed */}
            <div className="flex items-center gap-3">
              <div className="w-28 flex-shrink-0">
                <p className="text-xs font-medium text-primary">{language === 'pt' ? 'Minério Tratado' : 'Ore Processed'}</p>
                <p className="text-[10px] text-slate-400">Mt</p>
              </div>
              <div className="flex-1 flex gap-1 items-end h-16">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-primary mb-1">{cpcData.drivers.oreProcessed.value}</span>
                  <div className="w-full bg-gradient-to-t from-primary to-primary/80 rounded-t" style={{ height: `${(cpcData.drivers.oreProcessed.value / 2.8) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-gold-600 mb-1">{budgetOreProcessed}</span>
                  <div className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t" style={{ height: `${(budgetOreProcessed / 2.8) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] text-slate-400 mb-1">{prevCpcData.drivers.oreProcessed.value}</span>
                  <div className="w-full bg-gradient-to-t from-slate-300 to-slate-200 rounded-t" style={{ height: `${(prevCpcData.drivers.oreProcessed.value / 2.8) * 50}px` }}></div>
                </div>
              </div>
            </div>
            
            {/* Ore Moved (Minério Extraído) */}
            <div className="flex items-center gap-3">
              <div className="w-28 flex-shrink-0">
                <p className="text-xs font-medium text-primary">{language === 'pt' ? 'Minério Extraído' : 'Ore Moved'}</p>
                <p className="text-[10px] text-slate-400">M m³</p>
              </div>
              <div className="flex-1 flex gap-1 items-end h-16">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-primary mb-1">{cpcData.kpis.materialMoved.value}</span>
                  <div className="w-full bg-gradient-to-t from-primary to-primary/80 rounded-t" style={{ height: `${(cpcData.kpis.materialMoved.value / 22) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] font-bold text-gold-600 mb-1">{budgetOreMoved}</span>
                  <div className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t" style={{ height: `${(budgetOreMoved / 22) * 50}px` }}></div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-[9px] text-slate-400 mb-1">{prevCpcData.kpis.materialMoved.value}</span>
                  <div className="w-full bg-gradient-to-t from-slate-300 to-slate-200 rounded-t" style={{ height: `${(prevCpcData.kpis.materialMoved.value / 22) * 50}px` }}></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-3 mt-4 pt-3 border-t border-slate-100 text-[9px]">
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-primary rounded"></div><span>2025</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-gold-500 rounded"></div><span>Orç. 2025</span></div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-slate-300 rounded"></div><span>2024</span></div>
          </div>
        </div>
      </div>

      {/* Operational Drivers Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-2">
          {language === 'pt' ? 'Drivers Operacionais do Custo por Quilate' : 'Operational Drivers of Cost per Carat'}
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          {language === 'pt' 
            ? 'Cost per carat é função do denominador geológico-operacional, não apenas do numerador financeiro'
            : 'Cost per carat is a function of the geological-operational denominator, not just the financial numerator'}
        </p>
        
        <div className="grid grid-cols-3 gap-4">
          {/* Grade */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Grade</p>
            <p className="font-serif text-2xl font-semibold text-primary">{cpcData.drivers.grade.value}<span className="text-sm text-slate-400 ml-1">{cpcData.drivers.grade.unit}</span></p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${parseFloat(gradeVsBudget) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {gradeVsBudget >= 0 ? '+' : ''}{gradeVsBudget}% Orç.
              </span>
              <span className={`text-[10px] font-semibold ${cpcData.drivers.grade.vsLY >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {cpcData.drivers.grade.vsLY >= 0 ? '+' : ''}{cpcData.drivers.grade.vsLY}% YoY
              </span>
            </div>
          </div>

          {/* Strip Ratio */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Strip Ratio</p>
            <p className="font-serif text-2xl font-semibold text-primary">{cpcData.drivers.stripRatio.value}<span className="text-sm text-slate-400 ml-1">{cpcData.drivers.stripRatio.unit}</span></p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${parseFloat(stripRatioVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {stripRatioVsBudget >= 0 ? '+' : ''}{stripRatioVsBudget} vs Orç.
              </span>
              <span className={`text-[10px] font-semibold ${cpcData.drivers.stripRatio.vsLY <= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {cpcData.drivers.stripRatio.vsLY >= 0 ? '+' : ''}{cpcData.drivers.stripRatio.vsLY} YoY
              </span>
            </div>
          </div>

          {/* Plant Availability */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Disponib. Planta' : 'Plant Availability'}</p>
            <p className="font-serif text-2xl font-semibold text-primary">{cpcData.drivers.plantAvailability.value}<span className="text-sm text-slate-400 ml-1">{cpcData.drivers.plantAvailability.unit}</span></p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${parseFloat(plantAvailVsBudget) >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {plantAvailVsBudget >= 0 ? '+' : ''}{plantAvailVsBudget}% Orç.
              </span>
              <span className={`text-[10px] font-semibold ${cpcData.drivers.plantAvailability.vsLY >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {cpcData.drivers.plantAvailability.vsLY >= 0 ? '+' : ''}{cpcData.drivers.plantAvailability.vsLY}% YoY
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostPerCaratDetail;
